import { TripStageKey } from "@/features/trips/types";
import { Location, Point } from "@/features/types";

export type ProcessedLocation = {
  original: Location;
  coordinates: [number, number];
  timestampMs: number;
  index: number;
};

// Simplified frontend shape for per-stop delivery outcomes.
// This is intentionally close to MLTripSummary.stop_classification_summary.stop_summaries
// without mirroring the backend proto one-to-one.
export type CompletedStopStatus = "ON_TIME" | "EARLY" | "LATE" | "UNKNOWN";

export type CompletedStopSummary = {
  sequencePosition?: number;
  placeName?: string;
  status: CompletedStopStatus;
  deviationMinutes?: number;
};
export type ContractualWindowSnapshot = {
  windowStartHour: number;
  windowEndHour: number;
  routeType: string;
  timezone: string;
};

export type ContractualWindowUpdate = {
  eventTime: string;
  clusterId: string;
  windows: ContractualWindowSnapshot[];
};

export type PlaceContractualWindowHistory = {
  updates: ContractualWindowUpdate[];
};

export type EmpiricalWindowSnapshot = {
  windowIndex?: number;
  windowStartHour: number;
  windowEndHour: number;
  modeCenterH?: number;
  sampleCount?: number;
  timezone?: string;
};

export type EmpiricalWindowUpdate = {
  eventTime: string;
  clusterId: string;
  windows: EmpiricalWindowSnapshot[];
  calibrationTime?: string;
};

export type PlaceEmpiricalWindowHistory = {
  updates: EmpiricalWindowUpdate[];
};

export type PlaceDeliveryAlignment = {
  placeId: string;
  probability?: number;
  estimatedArrivalTime?: string;
  contractualOnTimeStatus?: string;
  contractualDeviationMinutes?: number;
  contractualRouteType?: string;
  empiricalOnTimeStatus?: string;
  empiricalDeviationMinutes?: number;
  empiricalWindowIndex?: number;
};

export type PlaceDeliveryAlignmentWithMetadata = PlaceDeliveryAlignment & {
  placeName?: string;
  location: Point;
  customerId?: string;
};

export type PlaceMarkerGroup = {
  location: Point;
  places: PlaceDeliveryAlignmentWithMetadata[];
};

export enum StopType {
  STOP_TYPE_UNKNOWN = "STOP_TYPE_UNKNOWN",
  STOP_TYPE_ARRIVAL = "STOP_TYPE_ARRIVAL",
  STOP_TYPE_PASS_THROUGH = "STOP_TYPE_PASS_THROUGH",
  STOP_TYPE_TERMINAL_ARRIVAL = "STOP_TYPE_TERMINAL_ARRIVAL",
}

export type ClassifiedStop = {
  stopId: string;
  clusterId: string;
  stopType: StopType | string;
  stopConfidence?: number;
  placeAlignments?: PlaceDeliveryAlignment[];
  contractualOnTimeStatus?: string;
  contractualOnTimeProbability?: number;
  contractualDeviationMinutes?: number;
  empiricalOnTimeStatus?: string;
  empiricalOnTimeProbability?: number;
  empiricalDeviationMinutes?: number;
  clusterEntryTime: string;
  clusterExitTime?: string;
  dwellDurationSeconds?: number;
};

export type ClassifiedStopWithMetadata = ClassifiedStop & {
  placeAlignments?: PlaceDeliveryAlignmentWithMetadata[];
  clusterLocation: Point;
  clusterRadiusMeters: number;
  clusterName?: string;
};

/** Contractual vs empirical delivery window benchmarking. */
export type DeliveryBenchmarkLane = "contractual" | "empirical";

export type StopBenchmarkStatusKey =
  | "contractualOnTimeStatus"
  | "empiricalOnTimeStatus";

export type StopBenchmarkDeviationKey =
  | "contractualDeviationMinutes"
  | "empiricalDeviationMinutes";

export type StopBenchmarkFields = {
  statusKey: StopBenchmarkStatusKey;
  deviationKey: StopBenchmarkDeviationKey;
};

export type StopBenchmarkFieldSource = Pick<
  ClassifiedStop,
  StopBenchmarkStatusKey | StopBenchmarkDeviationKey
>;

export type LaneAvailability = {
  hasContractual: boolean;
  hasEmpirical: boolean;
  hasAny: boolean;
};

export type DeliveryBenchmarkLockReason =
  | "contractualOnly"
  | "empiricalOnly"
  | "noBenchmarkData";

export type ResolvedDeliveryBenchmark = {
  lane: DeliveryBenchmarkLane;
  /** `true` when benchmarking against contractual delivery windows. */
  toggleChecked: boolean;
  isLocked: boolean;
  lockReason: DeliveryBenchmarkLockReason | null;
};

export type StopClassificationEvent = {
  eventId?: string;
  eventTime?: string;
  eventType?: string;
  stopId: string;
  clusterId?: string;
  stopType?: StopType | string;
  stopConfidence?: number;
  placeAlignments?: PlaceDeliveryAlignment[];
  contractualOnTimeStatus?: string;
  contractualOnTimeProbability?: number;
  contractualDeviationMinutes?: number;
  empiricalOnTimeStatus?: string;
  empiricalOnTimeProbability?: number;
  empiricalDeviationMinutes?: number;
  clusterEntryTime?: string;
  clusterExitTime?: string;
  dwellDurationSeconds?: number;
};

export type DwellSummary = {
  dwellId: string;
  clusterId: string;
  startTime: string;
  endTime: string;
  durationSeconds: number;
  /** e.g. "UNEXPECTED". Shown as badge in popup. */
  dwellType?: string;
};

export type DwellSummaryWithMetadata = DwellSummary & {
  centroidLatMicro: number;
  centroidLngMicro: number;
};

export type ETAQuantiles = {
  percentiles?: ETAQuantiles;
  [key: string]: number | ETAQuantiles | undefined;
};

/** Point metrics on ETA performance — partial; API may include more fields. */
export type ETAPointMetrics = {
  samples?: number;
  maeMinutes?: number;
  meanErrorMinutes?: number;
  p95AbsErrorMinutes?: number;
};

/**
 * Time-to-arrival bin used by `byTimeBin` rollups. Mirrors backend `ETATimeBin`.
 * Bin covers `(lowerExclusiveMinutes, upperInclusiveMinutes]` (or
 * `(lowerExclusiveMinutes, ∞)` when `isUpperOpenEnded`).
 */
export type ETATimeBin = {
  lowerExclusiveMinutes?: number;
  upperInclusiveMinutes?: number;
  isUpperOpenEnded?: boolean;
};

export type ETATimeBinMetrics = {
  bin?: ETATimeBin;
  pointMetrics?: ETAPointMetrics;
};

export type ETAStopPerformanceSummary = {
  clusterId?: string;
  clusterArrivalId?: string;
  /** 1-based position within the trip's scored-arrival sequence. */
  stopSequenceIndex?: number;
  pointMetrics?: ETAPointMetrics;
};

/** Subset of backend `ETATripPerformanceSummary` used by `computeEtaPerformance`. */
export type ETATripPerformanceSummary = {
  overallPointMetrics?: ETAPointMetrics;
  perStop?: ETAStopPerformanceSummary[];
  /** ETA error metrics bucketed by time-to-arrival horizon. */
  byTimeBin?: ETATimeBinMetrics[];
};

export type StopSummary = {
  stopId: string;
  clusterId: string;
  clusterName?: string;
  sequencePosition: number;
  stopType: StopType | string;
  arrivalTime?: string;
  departureTime?: string;
  dwellDurationSeconds?: number;
  contractualOnTimeStatus: string;
  contractualDeviationMinutes?: number;
  empiricalOnTimeStatus: string;
  empiricalDeviationMinutes?: number;
  lateDeliveryDetectedContractual?: boolean;
  lateDeliveryNoticeMinutesContractual?: number;
  lateDeliveryDetectedEmpirical?: boolean;
  lateDeliveryNoticeMinutesEmpirical?: number;
};

export type MLTripSummary = {
  tripId: string;
  currentStage: TripStageKey;
  startTime: string;
  endTime?: string;
  routeStopsCount?: number;
  totalDurationSeconds?: number;
  deliveryRouteDurationSeconds?: number;
  lateDeliveryCountContractual?: number;
  lateDeliveryCountEmpirical?: number;
  originClusterId: string;
  originClusterName?: string;
  destinationClusterId?: string;
  destinationClusterName?: string;
  stopSummaries: StopSummary[];
  hasActiveLateDeliveryNotification: boolean;
  predictedNextStopClusterId?: string;
  predictedNextStopClusterName?: string;
  predictedNextStopEta?: ETAQuantiles;
  /** Backend ETA error rollup; typed partially — only fields the web app reads. */
  etaPerformanceSummary?: ETATripPerformanceSummary;
  lastUpdated?: string;
  /** Present on some list payloads; full trips always include cluster metadata. */
  clusterMetadata?: Record<string, ClusterMetadata>;
};

export type PredictedRouteStop = {
  clusterId: string;
  memberPlaceIds?: string[];
  sequenceRank?: number;
  cumulativeSequenceRank?: number;
  visitCount?: number;
  stopType?: StopType | string;
  stopTypeConfidence?: number;
};

export type PredictedRoute = {
  predictionId: string;
  routePredictionRank?: number;
  confidence: number;
  stops: PredictedRouteStop[];
  matchedRouteId?: string;
  generatedAt?: string;
};

export type RoutePredictionSnapshot = {
  eventId: string;
  eventTime: string;
  predictedRoutes: PredictedRoute[];
  dataThroughTime?: string;
};

export type ETAStopPrediction = {
  targetClusterId: string;
  sequenceRank?: number;
  cumulativeSequenceRank?: number;
  visitCount?: number;
  routePredictionRank?: number;
  routePredictionId?: string;
  predictedMinutesRemaining?: number;
  quantiles?: Record<string, number>;
  stopType?: StopType | string;
  stopTypeConfidence?: number;
};

export type ETAPredictionSnapshot = {
  eventId?: string;
  eventTime: string;
  etaPredictions?: ETAStopPrediction[];
  dataThroughTime?: string;
};

export enum LDNEventType {
  LDN_EVENT_CREATED = "LDN_EVENT_CREATED",
  LDN_EVENT_RETRACTED = "LDN_EVENT_RETRACTED",
}

export type LDNEvent = {
  eventId?: string;
  eventTime: string;
  eventType: LDNEventType | string;
  notificationId?: string;
  clusterId: string;
  routePredictionRank?: number;
  routePredictionId?: string;
  sequenceRank?: number;
  cumulativeSequenceRank?: number;
  visitCount?: number;
  triggeredBy?: string[];
  estimatedArrival?: string;
  dataThroughTime?: string;
};

export type LDNInterval = {
  notificationId: string;
  clusterId: string;
  routePredictionRank?: number;
  visitCount?: number;
  triggeredBy?: string[];
  startTime: string;
  endTime?: string;
};

export type RawLocation = Omit<Location, "accuracyCentiMeters"> & {
  accuracyCentiMeters?: number;
};

export type LocationRecord = {
  location: RawLocation;
  modificationTime?: string;
};

export type Trajectory = {
  points: LocationRecord[];
};

/** Origin/destination place as returned by the Towers analytics API. */
export type TripPlace = {
  placeId: string;
  placeName: string;
  location: Point;
  radiusMeters?: number;
};
// ---------------------------------------------------------------------------
// MLTrip: single-entity trip (proto / Towers API aligned)
// ---------------------------------------------------------------------------

/** Place lookup map: placeId -> metadata (from placeMetadata in MLTrip). */
export type PlaceMetadata = {
  placeName?: string;
  location?: Point;
  customerId?: string;
  placeRole?: string;
  placeCategory?: string;
  clusterId?: string;
};

/** Cluster lookup map: clusterId -> metadata (from clusterMetadata in MLTrip). */
export type ClusterMetadata = {
  clusterName?: string;
  representativeName?: string;
  placeCount?: number;
  geofence?: {
    point?: Point;
    radiusMeters?: number;
  };
  memberPlaceIds?: string[];
  clusterType?: string;
};

const DEFAULT_CLUSTER_RADIUS_M = 600;

/**
 * Returns cluster centroid with fallbacks:
 * stop.clusterLocation → clusterMetadata[clusterId].geofence.point → (0, 0).
 */
export function getClusterLocation(
  stop: { clusterId: string; clusterLocation?: Point },
  clusterMetadata: Record<string, ClusterMetadata>,
): Point {
  if (stop.clusterLocation != null) {
    return stop.clusterLocation;
  }
  const meta = clusterMetadata[stop.clusterId];
  if (
    meta?.geofence?.point?.latitudeMicro != null &&
    meta.geofence.point.longitudeMicro != null
  ) {
    return meta.geofence.point;
  }
  return { latitudeMicro: 0, longitudeMicro: 0 };
}

/**
 * Returns cluster radius in meters with fallbacks:
 * stop.clusterRadiusMeters → clusterMetadata[clusterId].geofence.radiusMeters → 600.
 */
export function getClusterRadius(
  stop: { clusterId: string; clusterRadiusMeters?: number },
  clusterMetadata: Record<string, ClusterMetadata>,
): number {
  if (stop.clusterRadiusMeters != null) {
    return stop.clusterRadiusMeters;
  }
  const meta = clusterMetadata[stop.clusterId];
  return meta?.geofence?.radiusMeters ?? DEFAULT_CLUSTER_RADIUS_M;
}

/**
 * Full ML trip entity. Frontend consumes one trip object and derives
 * origin, destination, stops, trajectory via getOriginFromTrip, etc.
 */
export type MLTrip = {
  tripId: string;
  startTime: string;
  endTime?: string;
  currentStage?: TripStageKey | string;
  originPlaceIds: string[];
  originClusterId: string;
  originLatMicro?: number;
  originLngMicro?: number;
  destinationPlaceIds: string[];
  destinationClusterId?: string;
  destinationLatMicro?: number;
  destinationLngMicro?: number;
  trajectory: Trajectory;
  stopClassificationEvents: StopClassificationEvent[];
  dwells?: DwellSummary[];
  placeMetadata?: Record<string, PlaceMetadata>;
  clusterMetadata?: Record<string, ClusterMetadata>;
  placeContractualWindowHistory?: Record<string, PlaceContractualWindowHistory>;
  placeEmpiricalWindowHistory?: Record<string, PlaceEmpiricalWindowHistory>;
  routePredictionSnapshots: RoutePredictionSnapshot[];
  etaPredictionSnapshots?: ETAPredictionSnapshot[];
  ldnEvents?: LDNEvent[];
  contractualLdnIntervals?: LDNInterval[];
  empiricalLdnIntervals?: LDNInterval[];
};

function resolveTripEndpointLocation(
  trip: MLTrip,
  clusterId: string | undefined,
): Point {
  if (clusterId != null && clusterId !== "") {
    const cluster = trip.clusterMetadata?.[clusterId];
    if (
      cluster?.geofence?.point?.latitudeMicro != null &&
      cluster.geofence.point.longitudeMicro != null
    ) {
      return cluster.geofence.point;
    }
  }

  return {
    latitudeMicro: 0,
    longitudeMicro: 0,
  };
}

export function getOriginFromTrip(trip: MLTrip): TripPlace {
  const placeId = trip.originPlaceIds?.[0] ?? "";
  const name = trip.placeMetadata?.[placeId]?.placeName ?? "Unknown";
  return {
    placeId,
    placeName: name,
    location: resolveTripEndpointLocation(trip, trip.originClusterId),
    radiusMeters: 0,
  };
}

export function getDestinationFromTrip(trip: MLTrip): TripPlace {
  const placeId = trip.destinationPlaceIds?.[0] ?? "";
  const name = trip.placeMetadata?.[placeId]?.placeName ?? "Unknown";
  return {
    placeId,
    placeName: name,
    location: resolveTripEndpointLocation(trip, trip.destinationClusterId),
    radiusMeters: 0,
  };
}

/**
 * Resolve cluster geometry and place locations for a stop from trip metadata.
 * Use in components so API payloads can omit clusterLocation/clusterRadiusMeters.
 * Delegates to getClusterLocation / getClusterRadius for a single fallback chain.
 */
export function withResolvedStopGeometry(
  stop: ClassifiedStop,
  clusterMetadata?: Record<string, ClusterMetadata>,
  placeMetadata?: Record<string, PlaceMetadata>,
): ClassifiedStopWithMetadata {
  const metaMap = clusterMetadata ?? {};
  const clusterLocation = getClusterLocation(stop, metaMap);
  const clusterRadiusMeters = getClusterRadius(stop, metaMap);
  const metaName = metaMap[stop.clusterId]?.clusterName;
  const clusterName =
    metaName !== undefined && metaName !== null && metaName !== ""
      ? metaName
      : undefined;

  const placeAlignments = stop.placeAlignments?.map((p) => {
    const placeMeta = p.placeId ? placeMetadata?.[p.placeId] : undefined;
    const location: Point = placeMeta?.location ?? clusterLocation;

    return {
      ...p,
      placeName: placeMeta?.placeName,
      customerId: placeMeta?.customerId,
      location,
    };
  });

  return {
    ...stop,
    clusterLocation,
    clusterRadiusMeters,
    clusterName,
    placeAlignments,
  };
}

export function isLateStatus(status: string | undefined): boolean {
  return status === "ON_TIME_STATUS_LATE";
}

export function isTerminalArrivalStopType(
  stopType: string | undefined,
): boolean {
  const t = stopType ?? "";
  return t === "TERMINAL_ARRIVAL" || t === "STOP_TYPE_TERMINAL_ARRIVAL";
}

/**
 * Returns true if the given stop type is treated as an arrival stop.
 * Use this instead of inline checks so new/renamed arrival types are handled in one place.
 */
export function isArrivalStopType(stopType: string | undefined): boolean {
  const t = stopType ?? "";
  return (
    t === "ARRIVAL" ||
    t === StopType.STOP_TYPE_ARRIVAL ||
    isTerminalArrivalStopType(stopType)
  );
}

/**
 * Filter to STOP_TYPE_ARRIVAL events and enrich with cluster/place metadata.
 * Call in the component (e.g. TripViewPanel) so raw trip JSON can omit resolved geometry.
 */
export function enrichStopClassificationEventsWithMetadata(
  stops: StopClassificationEvent[],
  placeMetadata: Record<string, PlaceMetadata> = {},
  clusterMetadata: Record<string, ClusterMetadata> = {},
): ClassifiedStopWithMetadata[] {
  return reconstructLatestStopStatesByStopId(stops)
    .filter((stop) => isArrivalStopType(stop.stopType))
    .map((stop) =>
      withResolvedStopGeometry(stop, clusterMetadata, placeMetadata),
    );
}

function mergeStopEventState(
  previous: StopClassificationEvent | undefined,
  next: StopClassificationEvent,
): StopClassificationEvent {
  const merged = { ...(previous ?? {}) } as Record<string, unknown>;

  for (const [key, value] of Object.entries(next)) {
    if (value !== undefined) {
      merged[key] = value;
    }
  }

  return merged as StopClassificationEvent;
}

function isClassifiedStop(
  stop: StopClassificationEvent,
): stop is ClassifiedStop {
  return (
    stop.clusterId != null &&
    stop.clusterId !== "" &&
    stop.stopType != null &&
    stop.stopType !== "" &&
    stop.clusterEntryTime != null &&
    stop.clusterEntryTime !== ""
  );
}

export function reconstructLatestStopStatesByStopId(
  stops: StopClassificationEvent[] = [],
): ClassifiedStop[] {
  const latestStopStateByStopId = new Map<string, StopClassificationEvent>();

  for (const stop of stops) {
    if (stop.stopId == null || stop.stopId === "") {
      continue;
    }

    const previousStopState = latestStopStateByStopId.get(stop.stopId);
    latestStopStateByStopId.set(
      stop.stopId,
      mergeStopEventState(previousStopState, stop),
    );
  }

  return Array.from(latestStopStateByStopId.values())
    .filter(isClassifiedStop)
    .sort(
      (a, b) =>
        new Date(a.clusterEntryTime).getTime() -
        new Date(b.clusterEntryTime).getTime(),
    );
}

export function getStartTime(trip: MLTrip): Date {
  return new Date(trip.startTime);
}

export function getLatestTrajectoryTimestampMs(
  trajectory: Trajectory | undefined,
): number | null {
  const points = trajectory?.points ?? [];
  let latestTimestampMs: number | null = null;

  for (const point of points) {
    const timestampMs = new Date(point.location.locationTime).getTime();
    if (!Number.isFinite(timestampMs)) {
      continue;
    }

    if (latestTimestampMs == null || timestampMs > latestTimestampMs) {
      latestTimestampMs = timestampMs;
    }
  }

  return latestTimestampMs;
}

export function getEndTime(trip: MLTrip): Date {
  // 1) Prefer backend-provided endTime when present and parseable.
  if (trip.endTime != null && trip.endTime !== "") {
    const endTime = new Date(trip.endTime);
    if (!Number.isNaN(endTime.getTime())) {
      return endTime;
    }
  }

  // 2) For in-progress trips (or missing endTime), use latest trajectory point time.
  const latestTrajectoryTimestampMs = getLatestTrajectoryTimestampMs(
    trip.trajectory,
  );
  if (latestTrajectoryTimestampMs != null) {
    return new Date(latestTrajectoryTimestampMs);
  }

  // 3) If trajectory is missing, fall back to startTime to keep UI timelines valid.
  const startTime = new Date(trip.startTime);
  if (!Number.isNaN(startTime.getTime())) {
    return startTime;
  }

  // 4) Last-resort guard against completely malformed payloads.
  return new Date();
}

// ---------------------------------------------------------------------------
// Root-cause analysis (RCA) types — per-delivery factor decomposition
// ---------------------------------------------------------------------------

export type RootCauseFactorSummary = {
  mean: number;
  ciLow: number;
  ciHigh: number;
  nullP: number;
};

export function isValidSummary(
  summary: RootCauseFactorSummary | Record<string, never> | undefined,
): summary is RootCauseFactorSummary {
  return !!summary && "mean" in summary;
}

export type RcaPairFactors = {
  departure: number;
  commonDwell: number;
  commonTransit: number;
  additionalDwell: number;
  additionalTransit: number;
};

export type RcaPair = {
  baselineIdx: number;
  diMin: number;
  factors: RcaPairFactors;
};

export type LateDeliveryRootCause = {
  tripId: string;
  clusterId: string;
  lane: "RCA_LANE_CONTRACTUAL" | "RCA_LANE_EMPIRICAL";
  nBaselines: string;
  depSummary: RootCauseFactorSummary | Record<string, never>;
  commonDwellSummary: RootCauseFactorSummary | Record<string, never>;
  commonTransitSummary: RootCauseFactorSummary | Record<string, never>;
  additionalDwellSummary: RootCauseFactorSummary | Record<string, never>;
  additionalTransitSummary: RootCauseFactorSummary | Record<string, never>;
  candidatePairs: RcaPair[];
  nullPairs: RcaPair[];
};

export type StopDetailRcaProps = {
  tripSummaries: MLTripSummary[];
  clusterId: string;
  /** Resolved on the page; must match the header delivery benchmark toggle. */
  deliveryBenchmarkLane: "contractual" | "empirical";
  isLoading: boolean;
  isFetching: boolean;
};
